import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MetaIntegration } from '@feathr/blackbox';
import { ConfirmModal, ContextMenu, toast } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import { errorMessage, getIconForAction, logError, useToggle } from '@feathr/hooks';

import { useMetaSync } from '../../MetaIntegrationPage.hooks';
import { useMetaOAuth } from '../../PreConnection/MetaConnectButton/MetaConnectButton.hooks';

interface IMetaSettingsButtonProps {
  integration: MetaIntegration;
  onDisconnect: () => Promise<void>;
}

function MetaSettingsButton({
  integration,
  onDisconnect,
}: Readonly<IMetaSettingsButtonProps>): JSX.Element {
  const { t } = useTranslation();
  const [showModal, toggleModal] = useToggle(false);
  const { handleSync, isSyncing } = useMetaSync(integration);
  const { initiateOAuth, isLoading } = useMetaOAuth();
  const user = useUser();

  async function handleDisconnect(): Promise<void> {
    try {
      await integration.disconnect();
    } catch (error) {
      const errorMsg = errorMessage(error, t);
      logError({
        message: 'Meta integration disconnect failed',
        level: 'error',
        extra: {
          error: errorMsg,
          integrationId: integration?.id,
        },
      });
      toast(
        t("Couldn't disconnect Meta. {{error}}", {
          error: errorMsg,
        }),
        { type: 'error' },
      );
      toggleModal();
      return;
    }

    toast(t('Disconnected Meta.'), { type: 'success' });
    toggleModal();
    await onDisconnect();
  }

  return (
    <>
      <ContextMenu
        buttonText={t('Settings')}
        buttonType={'secondary'}
        disabled={isLoading}
        name={'meta-settings'}
        suffix={getIconForAction('settings')}
      >
        <ContextMenu.Item onClick={initiateOAuth} prefix={getIconForAction('configure')}>
          {t('Reconfigure')}
        </ContextMenu.Item>
        {user.isSudoer && (
          <ContextMenu.Item
            disabled={isSyncing}
            onClick={handleSync}
            prefix={getIconForAction('recalculate')}
          >
            {t('Synchronize')}
          </ContextMenu.Item>
        )}
        <ContextMenu.Divider />
        <ContextMenu.Item
          onClick={toggleModal}
          prefix={getIconForAction('disconnect')}
          theme={'danger'}
        >
          {t('Disconnect')}
        </ContextMenu.Item>
      </ContextMenu>
      <ConfirmModal
        alertTitle={t('You will lose the ability to publish Meta campaigns.')}
        alertType={'danger'}
        confirmButtonText={t('Disconnect')}
        confirmButtonType={'danger'}
        onClose={toggleModal}
        onConfirm={handleDisconnect}
        opened={showModal}
        t={t}
        title={t('Disconnect Meta integration?')}
      />
    </>
  );
}

export default MetaSettingsButton;
